/**
 * JOAdminBrandRenamer — rewrites residual "COCKPIT" labels in the admin
 * sidebar to "Digipilote" without touching the core AdminLayout.jsx.
 *
 * Targets:
 *   - The brand block under [data-testid="admin-sidebar-cockpit"] which
 *     hardcodes the literal "COCKPIT" text node.
 *   - The version/footer pill [data-testid="sidebar-cockpit-version"] also
 *     contains the literal label (server-driven, but defensive).
 *
 * Renderless component — runs in useEffect on every admin route change
 * and also uses a MutationObserver to catch lazy-rendered nodes.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const FROM = /\bCOCKPIT\b/;
const TO = "Digipilote";
const MARKER = "data-jdo-brand-renamed";

function rewriteSubtree(root) {
  if (!root) return;
  // Walk text nodes only
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const targets = [];
  while (walker.nextNode()) {
    const node = walker.currentNode;
    if (node.textContent && FROM.test(node.textContent)) {
      targets.push(node);
    }
  }
  for (const node of targets) {
    node.textContent = node.textContent.replace(FROM, TO);
  }
  if (targets.length) root.setAttribute && root.setAttribute(MARKER, "yes");
}

export default function JOAdminBrandRenamer() {
  const location = useLocation();

  useEffect(() => {
    if (!location.pathname.startsWith("/admin")) return;

    const run = () => {
      // 1. Sidebar brand pill
      rewriteSubtree(document.querySelector('[data-testid="admin-sidebar-cockpit"]'));
      // 2. Sidebar version label (server-driven but defensive)
      rewriteSubtree(document.querySelector('[data-testid="sidebar-cockpit-version"]'));
    };

    run();
    // Re-run a few times — the sidebar mounts after auth resolution
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      run();
      if (tries > 15) clearInterval(interval);
    }, 250);

    // Observe DOM for lazy-rendered sidebar fragments
    const observer = new MutationObserver(() => run());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [location.pathname]);

  return null;
}
